import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ParticlesBackground from '../../components/ParticlesBackground';
import PresentationSlide1 from './PresentationSlide1';
import PresentationSlide2 from './PresentationSlide2';
import PresentationSlide3 from './PresentationSlide3';
import PresentationSlide4 from './PresentationSlide4';
import PresentationSlide5 from './PresentationSlide5';
import PresentationSlide6 from './PresentationSlide6';
import PresentationSlide7 from './PresentationSlide7';

// Componentes estilizados
const PresentationContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SlideContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  height: 100%;
  overflow: hidden;
`;

const AnimatedSlideContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const NavigationControls = styled.div`
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  padding: 12px 20px;
  border-radius: 15px;
  margin: 0 auto;
  width: fit-content;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 182, 255, 0.3);
`;

const NavButton = styled.button`
  background: rgba(0, 150, 255, 0.3);
  color: #64B6FF;
  border: 1px solid #64B6FF;
  border-radius: 5px;
  padding: 8px 15px;
  cursor: pointer;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #64B6FF);
    animation: scanline 2s linear infinite;
  }
  
  &:hover {
    background: rgba(0, 150, 255, 0.5);
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.7);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @keyframes scanline {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const SlideIndicator = styled.div`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  font-size: 14px;
  letter-spacing: 1px;
  margin: 0 10px;
`;

// Variantes de animação para transições tecnológicas
const slideVariants = {
  // Entrada vindo da direita (avançar)
  enterFromRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Entrada vindo da esquerda (voltar)
  enterFromLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Estado central (slide atual)
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Saída para a esquerda (avançar)
  exitToLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeIn"
    }
  },
  // Saída para a direita (voltar)
  exitToRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeIn"
    }
  }
};

// Variante para o efeito de glitch tecnológico - simplificado e acelerado
const glitchAnimation = {
  hidden: {
    clipPath: "inset(100% 0 0 0)",
  },
  visible: {
    clipPath: "inset(0 0 0 0)",
    transition: { 
      duration: 0.25,
      ease: "easeOut"
    }
  },
  exit: {
    clipPath: "inset(0 0 100% 0)",
    transition: { 
      duration: 0.20,
      ease: "easeIn"
    }
  }
};

const PresentationPage = () => {
  const { slideNumber } = useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(parseInt(slideNumber) || 1);
  const [direction, setDirection] = useState("forward"); // Controla a direção da transição
  const [hasInitialized, setHasInitialized] = useState(false);
  const totalSlides = 7;
  
  // Atualiza o slide quando o parâmetro da URL muda
  useEffect(() => {
    if (slideNumber) {
      const slideNum = parseInt(slideNumber);
      
      if (slideNum >= 1 && slideNum <= totalSlides) {
        // Define a direção com base na mudança de slide
        if (hasInitialized) {
          if (slideNum > currentSlide) {
            setDirection("forward");
          } else if (slideNum < currentSlide) {
            setDirection("backward");
          }
        }
        
        setCurrentSlide(slideNum);
      } else {
        navigate('/apresentacao/1', { replace: true });
      }
    } else {
      navigate('/apresentacao/1', { replace: true });
    }
    
    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [slideNumber, navigate, currentSlide, hasInitialized, totalSlides]);
  
  // Navegação com teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentSlide < totalSlides) {
        setDirection("forward");
        navigate(`/apresentacao/${currentSlide + 1}`);
      } else if (e.key === 'ArrowLeft' && currentSlide > 1) {
        setDirection("backward");
        navigate(`/apresentacao/${currentSlide - 1}`);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, navigate, totalSlides]);
  
  // Função para navegar para o próximo slide
  const goToNextSlide = () => {
    if (currentSlide < totalSlides) {
      setDirection("forward");
      navigate(`/apresentacao/${currentSlide + 1}`);
    }
  };
  
  // Função para navegar para o slide anterior
  const goToPrevSlide = () => {
    if (currentSlide > 1) {
      setDirection("backward");
      navigate(`/apresentacao/${currentSlide - 1}`);
    }
  };
  
  // Renderiza o slide atual
  const renderCurrentSlide = () => {
    switch (currentSlide) {
      case 1:
        return <PresentationSlide1 />;
      case 2:
        return <PresentationSlide2 />;
      case 3:
        return <PresentationSlide3 />;
      case 4:
        return <PresentationSlide4 />;
      case 5:
        return <PresentationSlide5 />;
      case 6:
        return <PresentationSlide6 />;
      case 7:
        return <PresentationSlide7 />;
      default:
        return <PresentationSlide1 />;
    }
  };
  
  return (
    <PresentationContainer>
      <ParticlesBackground />
      <SlideContainer>
        <AnimatePresence mode="wait" initial={false}>
          <AnimatedSlideContainer
            key={currentSlide}
            initial={currentSlide === 1 && !hasInitialized ? "center" : (direction === "forward" ? "enterFromRight" : "enterFromLeft")}
            animate="center"
            exit={direction === "forward" ? "exitToLeft" : "exitToRight"}
            variants={slideVariants}
          >
            <motion.div
              style={{ width: '100%', height: '100%' }}
              initial={currentSlide === 1 && !hasInitialized ? "visible" : "hidden"}
              animate="visible"
              exit="exit"
              variants={glitchAnimation}
            >
              {renderCurrentSlide()}
            </motion.div>
          </AnimatedSlideContainer>
        </AnimatePresence>
      </SlideContainer>
      
      <NavigationControls>
        <NavButton 
          onClick={goToPrevSlide} 
          disabled={currentSlide === 1}
        >
          Anterior
        </NavButton>
        <SlideIndicator>{currentSlide} / {totalSlides}</SlideIndicator>
        <NavButton 
          onClick={goToNextSlide} 
          disabled={currentSlide === totalSlides}
        >
          Próximo
        </NavButton>
      </NavigationControls>
    </PresentationContainer>
  );
};

export default PresentationPage;
